import { useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Typography,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Snackbar,
    Alert,
} from "@mui/material";
import { config } from "../Const";
import { executeFetch, executeFetchAsync } from "../Util";
import { getAttributionText, getAttributionURLs } from "./attributionHelpers";
import PinterestIcon from "@mui/icons-material/Pinterest";
import { getPinterestBoardIdByTitle } from "./categoryUtil";

export default function PinterestPin({ detailedEntity, pinterestUrl, pageUrl, publishedTokenList }) {
    const quizLabel = detailedEntity?.label;
    const questionCount = detailedEntity?.questionList?.length;
    const pinterestImageMetadata = detailedEntity?.pinterestImageMetadata;

    const [subject, setSubject] = useState();
    const [nOptions, setNOptions] = useState(3);
    const [generating, setGenerating] = useState(false); // for generate options
    const [rawOptions, setRawOptions] = useState([]); // objects { title, description } but title will be ignored
    const [dialogOpen, setDialogOpen] = useState(false);

    // removed titleIdx state - title is always quizLabel
    const [descIdx, setDescIdx] = useState(0);
    const [editedTitle, setEditedTitle] = useState("");
    const [editedDescription, setEditedDescription] = useState("");
    const [chosen, setChosen] = useState(null);

    // new state for posting pin
    const [postingPin, setPostingPin] = useState(false);
    const [pinSnackbarOpen, setPinSnackbarOpen] = useState(false);
    const [pinSnackbarMsg, setPinSnackbarMsg] = useState("");

    // boards fetched from server
    const [boardList, setBoardList] = useState([]); // [{id,label}, ...]
    const [boardsLoading, setBoardsLoading] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [connectionLoading, setConnectionLoading] = useState(true);
    const [loginPending, setLoginPending] = useState(false);

    // selected Pinterest board (string)
    const [selectedBoardId, setSelectedBoardId] = useState(null);

    const apiUrl = `${config.SERVER_URL}/api/v1/admin/AI/generate-pinterest-pin`;
    const createPinApi = `${config.SERVER_URL}/api/v1/admin/pinterest/create-pin`;
    const listBoardsApi = `${config.SERVER_URL}/api/v1/admin/pinterest/list-boards`;
    const pinterestAuthorizationUrlApi = `${config.SERVER_URL}/api/v1/admin/pinterest/authorization-url`;
    const pinterestConnectionStatusApi = `${config.SERVER_URL}/api/v1/admin/pinterest/connection-status`;

    const getPublishDateFromUrl = (url) => {
        if (!publishedTokenList) return null;
        let publishToken = url?.replace(pageUrl + "?t=", "");
        let publishDate = publishedTokenList.find((pt) => pt.token === publishToken)?.publishDate;
        return publishDate;
    };

    useEffect(() => {
        setSubject(detailedEntity?.label);
    }, [detailedEntity]);

    // Keep refs for popup and poll interval so we can clear them
    const popupRef = useRef(null);
    const pollRef = useRef(null);
    const authResultHandledRef = useRef(false);

    const fetchConnectionStatus = useCallback(async () => {
        setConnectionLoading(true);
        try {
            const status = await executeFetchAsync(
                pinterestConnectionStatusApi, "get", null, null);
            setConnectionStatus(status);
            return status;
        } catch (err) {
            console.error("Failed to fetch Pinterest connection status:", err);
            setConnectionStatus(null);
            return null;
        } finally {
            setConnectionLoading(false);
        }
    }, [pinterestConnectionStatusApi]);

    // extracted fetchBoards so we can call it from mount and after login
    const fetchBoards = useCallback(async () => {
        setBoardsLoading(true);
        try {
            const result = await executeFetchAsync(listBoardsApi, "get", null, null);
            const items = result.items;

            const arr = [];

            for (const { id, name } of items) {
                if (id) arr.push({ id, name });
            }

            console.log(arr);

            setBoardList(arr);

            // default to first board if none selected yet
            setSelectedBoardId((prev) => (prev ? prev : arr.length > 0 ? arr[0].id : null));
        } catch (err) {
            console.error("Failed to fetch Pinterest boards:", err);
            setPinSnackbarMsg("Failed to load Pinterest boards");
            setPinSnackbarOpen(true);
        } finally {
            setBoardsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listBoardsApi, executeFetchAsync]);

    // Load boards only after confirming that the server has a usable credential.
    useEffect(() => {
        let active = true;
        const initializePinterest = async () => {
            const status = await fetchConnectionStatus();
            if (active && status?.connected) {
                await fetchBoards();
            }
        };
        void initializePinterest();
        return () => { active = false; };
    }, [fetchBoards, fetchConnectionStatus]);

    const safeParseOptions = (bodyStr) => {
        if (!bodyStr) return [];
        try {
            const parsed = JSON.parse(bodyStr);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) {
            console.log(e);
        }
        return [];
    };

    const normalizeOptions = (parsedArray) => {
        return parsedArray.map((o) => {
            if (typeof o === "string") {
                return { title: o, description: "" };
            }
            return {
                title: o.title ?? "",
                description: o.description ?? "",
            };
        });
    };

    const fetchOptions = () => {
        setGenerating(true);

        const formData = new FormData();
        formData.append("subject", (subject || "").trim());
        formData.append("count", String(nOptions));
        formData.append("questionCount", String(questionCount));

        const onSuccess = (result) => {
            const bodyStr = (result && (result.body ?? result)) || "";
            const parsed = safeParseOptions(bodyStr);
            const normalized = normalizeOptions(parsed);

            if (normalized.length === 0) {
                normalized.push({
                    title: `Quiz: ${subject}`,
                    description: `Click to test your knowledge about ${subject}.`,
                });
            }

            setRawOptions(normalized);

            // Description defaults
            setDescIdx(0);
            // Title must always be quiz label; set editedTitle from quizLabel (fall back to normalized[0].title)
            setEditedTitle(quizLabel ?? (normalized[0]?.title ?? ""));
            setEditedDescription(normalized[0]?.description ?? "");
            setGenerating(false);
        };

        const onError = () => {
            setGenerating(false);
            console.error("Failed to generate Pinterest options");
        };

        executeFetch(apiUrl, "post", null, formData, null, onSuccess, onError);
    };

    const handleGenerateClick = () => {
        if (!subject?.trim()) return;

        if (rawOptions && rawOptions.length > 0) {
            setDialogOpen(true);
            return;
        }

        setDialogOpen(true);
        fetchOptions();
    };

    // description list only (title is fixed to quizLabel)
    const descList = rawOptions.map((o) => o.description ?? "");

    const handleDescSelect = (ev) => {
        const idx = Number(ev.target.value);
        setDescIdx(idx);
    };

    const handleBoardSelect = (ev) => {
        setSelectedBoardId(String(ev.target.value));
    };

    const handleConfirmSelection = async () => {
        // Title always from quizLabel (or fallback to editedTitle)
        const selTitle = quizLabel ?? editedTitle ?? "";
        const selDesc = descList[descIdx] ?? "";

        let finalDesc = selDesc;
        try {
            if (detailedEntity) {
                const attrText = await getAttributionText(pinterestImageMetadata);
                if (attrText && attrText.trim().length > 0) {
                    finalDesc = finalDesc ? `${finalDesc}\n\n${attrText.trim()}` : attrText.trim();
                }

                const urls = await getAttributionURLs(pinterestImageMetadata);
                const linkLines = [];
                if (urls?.photoUrl) linkLines.push(`Photo: ${urls.photoUrl}`);
                if (urls?.artistUrl) linkLines.push(`Artist: ${urls.artistUrl}`);
                if (urls?.licenseUrl) linkLines.push(`License: ${urls.licenseUrl}`);

                if (linkLines.length > 0) {
                    finalDesc = finalDesc ? `${finalDesc}\n\n${linkLines.join("\n")}` : linkLines.join("\n");
                }
            }
        } catch (e) {
            console.error("Failed to fetch attribution:", e);
        }

        setEditedTitle(selTitle);
        setEditedDescription(finalDesc);
        setChosen({ title: selTitle, description: finalDesc });
        setDialogOpen(false);
    };

    const handleGeneratePin = async () => {
        const seoTitle = detailedEntity?.seoTitle || "";
        // ensure title equals quizLabel
        const title = (detailedEntity?.label || "").trim();
        const description = editedDescription?.trim() || "";

        if (!seoTitle) {
            setPinSnackbarMsg("Missing quiz seoTitle");
            setPinSnackbarOpen(true);
            return;
        }
        if (!title || !description) {
            setPinSnackbarMsg("Please choose or edit a title and description first");
            setPinSnackbarOpen(true);
            return;
        }

        if (!selectedBoardId) {
            setPinSnackbarMsg("Please select a Pinterest board");
            setPinSnackbarOpen(true);
            return;
        }

        setPostingPin(true);
        try {
            const formData = new FormData();
            formData.append("link", pinterestUrl);
            formData.append("pinterestImageMetadata", JSON.stringify(pinterestImageMetadata || {}));
            formData.append("boardId", selectedBoardId);
            formData.append("title", title);
            formData.append("description", description);

            await executeFetchAsync(createPinApi, "post", null, formData);

            setPinSnackbarMsg("Pin generated successfully");
            setPinSnackbarOpen(true);
        } catch (err) {
            console.error("Failed to generate pin:", err);
            const status = await fetchConnectionStatus();
            setPinSnackbarMsg(status && !status.connected
                ? "Pinterest authorization expired. Please reconnect."
                : "Failed to generate pin");
            setPinSnackbarOpen(true);
        } finally {
            setPostingPin(false);
        }
    };

    const handleRefresh = () => {
        if (!subject?.trim()) return;
        fetchOptions();
    };

    const pinterestFile = pinterestImageMetadata?.storedImageFileNameSocialMap?.PINTEREST;
    const pinterestImageUrl = pinterestFile ? `${config.CDN_URL.replace(/\/$/, "")}/${pinterestFile.replace(/^\/+/, "")}` : null;

    const handlePinSnackbarClose = (event, reason) => {
        if (reason === "clickaway") return;
        setPinSnackbarOpen(false);
    };

    const finishPinterestLogin = useCallback(async (success, reason) => {
        if (authResultHandledRef.current) return;
        authResultHandledRef.current = true;
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        popupRef.current = null;
        setLoginPending(false);

        if (!success) {
            await fetchConnectionStatus();
            setPinSnackbarMsg(`Pinterest login failed${reason ? `: ${reason}` : ""}`);
            setPinSnackbarOpen(true);
            return;
        }

        const status = await fetchConnectionStatus();
        if (status?.connected) {
            await fetchBoards();
            setPinSnackbarMsg("Pinterest connected — boards refreshed");
        } else {
            setPinSnackbarMsg("Pinterest login completed, but no active connection was stored");
        }
        setPinSnackbarOpen(true);
    }, [fetchBoards, fetchConnectionStatus]);

    useEffect(() => {
        const serverOrigin = new URL(config.SERVER_URL, window.location.href).origin;
        const handleAuthMessage = (event) => {
            if (event.origin !== serverOrigin || event.source !== popupRef.current) return;
            if (event.data?.type !== "pinterest_auth") return;
            void finishPinterestLogin(Boolean(event.data.success), event.data.reason);
        };
        window.addEventListener("message", handleAuthMessage);
        return () => window.removeEventListener("message", handleAuthMessage);
    }, [finishPinterestLogin]);

    const handlePinterestLogin = async () => {
        if (popupRef.current && !popupRef.current.closed) {
            try { popupRef.current.focus(); } catch (e) { /* ignore */ }
            return;
        }

        const features = "width=420,height=820,menubar=no,toolbar=no,status=no";
        // Open synchronously so the browser does not block the popup while the URL is fetched.
        const win = window.open("", "pinterestLogin", features);
        if (!win) {
            setPinSnackbarMsg("Please allow popups to connect Pinterest");
            setPinSnackbarOpen(true);
            return;
        }
        popupRef.current = win;
        authResultHandledRef.current = false;
        setLoginPending(true);

        try {
            const result = await executeFetchAsync(
                pinterestAuthorizationUrlApi, "get", null, null);
            if (!result?.authorizationUrl) {
                throw new Error("Server did not return a Pinterest authorization URL");
            }
            win.location.replace(result.authorizationUrl);
        } catch (err) {
            try { win.close(); } catch (e) { /* ignore */ }
            popupRef.current = null;
            setLoginPending(false);
            setPinSnackbarMsg(`Could not start Pinterest login: ${err.message}`);
            setPinSnackbarOpen(true);
            return;
        }

        // Fallback for browsers that close the popup before delivering postMessage.
        pollRef.current = setInterval(() => {
            try {
                if (!popupRef.current || popupRef.current.closed) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    popupRef.current = null;
                    if (!authResultHandledRef.current) {
                        void finishPinterestLogin(true);
                    }
                }
            } catch (err) {
                // Cross-origin popup access may briefly fail while Pinterest is open.
            }
        }, 600);

        try { win.focus(); } catch (e) { /* ignore */ }
    };

    // cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            if (popupRef.current && !popupRef.current.closed) {
                try { popupRef.current.close(); } catch (e) { /* ignore */ }
            }
        };
    }, []);


    useEffect(() => {

        if (!boardList || boardList.length === 0) return;
        if (!detailedEntity) return;

        const tagCandidates = [];

        if (Array.isArray(detailedEntity.tagList)) {
            detailedEntity.tagList.forEach((t) => {
                if (!t) return;
                if (typeof t === "string") tagCandidates.push(t);
                else if (typeof t === "object") {
                    if (t.name) tagCandidates.push(t.name);
                    else if (t.title) tagCandidates.push(t.title);
                    else if (t.label) tagCandidates.push(t.label);
                }
            });
        }

        const normalized = Array.from(new Set(tagCandidates.filter(Boolean).map((s) => String(s).trim())));

        // find first matching board id that we actually have in boardList
        for (const candidate of normalized) {
            const boardId = getPinterestBoardIdByTitle(candidate);
            if (boardId && boardList.some((b) => String(b.id) === String(boardId))) {
                setSelectedBoardId(String(boardId));
                return;
            }
        }

    }, [boardList, detailedEntity]);

    // --- NEW: compute publish date for pinterestUrl (if any)
    const pinterestPublishDate = getPublishDateFromUrl(pinterestUrl);
    const pinterestPublishDateFormatted = pinterestPublishDate ? new Date(pinterestPublishDate).toLocaleString() : null;

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 320, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField
                            label="Pinterest subject"
                            value={subject || ""}
                            onChange={(e) => setSubject(e.target.value)}
                            size="small"
                            sx={{ minWidth: 420 }}
                        />
                        <TextField
                            label="# options"
                            type="number"
                            value={nOptions}
                            onChange={(e) => setNOptions(Math.max(1, Math.min(10, Number(e.target.value) || 3)))}
                            size="small"
                            sx={{ width: 110 }}
                        />
                        <Button variant="contained" onClick={handleGenerateClick} disabled={!subject || generating}>
                            {generating ? <CircularProgress size={18} /> : "Generate"}
                        </Button>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
                            <InputLabel id="p-board-select-label">Pinterest Board</InputLabel>
                            <Select
                                labelId="p-board-select-label"
                                value={selectedBoardId ?? ""}
                                label="Pinterest Board"
                                onChange={handleBoardSelect}
                                disabled={!connectionStatus?.connected || boardsLoading}
                                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                            >
                                {boardsLoading ? (
                                    <MenuItem value="" disabled>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CircularProgress size={16} /> Loading boards...
                                        </Box>
                                    </MenuItem>
                                ) : boardList.length === 0 ? (
                                    <MenuItem value="" disabled>No boards available</MenuItem>
                                ) : (
                                    boardList.map((b) => (
                                        <MenuItem key={b.id} value={b.id}>
                                            <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{b.name}</Box>
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Button
                                startIcon={<PinterestIcon />}
                                onClick={handlePinterestLogin}
                                disabled={postingPin || loginPending}
                                aria-label="Login to Pinterest"
                            >
                                {loginPending
                                    ? <CircularProgress size={18} />
                                    : connectionStatus?.connected ? "Reconnect" : "Connect"}
                            </Button>
                        </Box>
                    </Box>


                    {!connectionLoading && (
                        <Alert severity={connectionStatus?.connected ? "success" : "warning"}>
                            {connectionStatus?.connected
                                ? `Pinterest connected${connectionStatus.accessTokenExpiresAt
                                    ? ` · access token expires ${new Date(connectionStatus.accessTokenExpiresAt).toLocaleString()}`
                                    : ""}`
                                : connectionStatus?.status === "REAUTH_REQUIRED"
                                    ? "Pinterest authorization expired. Reconnect to resume publishing."
                                    : "Pinterest is not connected."}
                        </Alert>
                    )}

                    <Box>
                        {chosen ? (
                            <Paper sx={{ p: 2, mb: 0 }}>
                                <Typography variant="h6">Title</Typography>
                                <TextField
                                    fullWidth
                                    value={detailedEntity?.label ?? editedTitle}
                                    // readonly: title must equal quiz label
                                    InputProps={{ readOnly: true }}
                                    size="small"
                                    sx={{ mb: 2 }}
                                />

                                <Typography variant="h6">Description</Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    sx={{ mb: 2 }}
                                />

                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button variant="contained" onClick={handleGeneratePin}
                                            disabled={postingPin || !connectionStatus?.connected}>
                                        {postingPin ? <CircularProgress size={18} /> : "Create Pin"}
                                    </Button>
                                </Box>
                            </Paper>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No selected Pinterest text yet — generate options to choose from.
                            </Typography>
                        )}
                    </Box>

                    {/* show published date for the pinterestUrl if it's already published */}
                    {pinterestUrl && pinterestPublishDateFormatted && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="h4" color="success.main">
                                Published: {pinterestPublishDateFormatted}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* RIGHT COLUMN: image preview */}
                <Box sx={{ width: 240, minWidth: 160, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {pinterestImageUrl ? (
                        <Box
                            component="img"
                            src={pinterestImageUrl}
                            alt={quizLabel ? `${quizLabel} (Pinterest)` : "Pinterest image"}
                            sx={{
                                width: "100%",
                                borderRadius: 1,
                                boxShadow: 1,
                                border: "1px solid rgba(0,0,0,0.08)",
                            }}
                            loading="lazy"
                        />
                    ) : (
                        <Box
                            sx={{
                                width: "100%",
                                height: 140,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "background.paper",
                                border: "1px dashed rgba(0,0,0,0.08)",
                                borderRadius: 1,
                                p: 1,
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">No Pinterest image available</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Dialog (Title is readonly quiz label; Description selection only) */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Choose Title & Description</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Box>
                            {/* Title: always show quiz label in readonly field */}
                            <TextField
                                label="Title (readonly)"
                                fullWidth
                                size="small"
                                value={quizLabel ?? ""}
                                InputProps={{ readOnly: true }}
                                sx={{ mb: 1 }}
                            />
                        </Box>

                        <Box>
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                <InputLabel id="p-desc-select-label">Description</InputLabel>
                                <Select
                                    labelId="p-desc-select-label"
                                    value={descIdx}
                                    label="Description"
                                    onChange={handleDescSelect}
                                    MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                                >
                                    {descList.map((d, idx) => (
                                        <MenuItem key={`desc-${idx}`} value={idx} sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                            <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{d || "(no description)"}</Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>

                    <Button onClick={handleRefresh} disabled={generating}>
                        {generating ? <CircularProgress size={18} /> : "Refresh"}
                    </Button>

                    <Button variant="contained" onClick={handleConfirmSelection}>Use selection</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={pinSnackbarOpen} autoHideDuration={3000} message={pinSnackbarMsg} onClose={handlePinSnackbarClose} />
        </Box>
    );
}
